import type { Recurrence } from "@/api/types";

/**
 * Datas de recorrência calculadas no cliente com a MESMA regra do servidor
 * (`apps/api/src/finance/recurrences/rules.ts`): dia 31 em mês curto → último dia; semanal com 0 = domingo.
 * A API não expõe a próxima data; o Início usa isto para "próximos 7 dias".
 */
type CivilDate = string;

const pad = (n: number) => String(n).padStart(2, "0");
const lastDayOfMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const clamped = (year: number, month: number, day: number): CivilDate =>
  `${year}-${pad(month)}-${pad(Math.min(day, lastDayOfMonth(year, month)))}`;

export function addDays(date: CivilDate, days: number): CivilDate {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

type Rule = Pick<Recurrence, "frequency" | "dayOfMonth" | "weekday" | "month">;

/** Datas da regra em [from, to] (inclusivo), em ordem — sem olhar início/fim da recorrência. */
export function occurrencesBetween(rule: Rule, from: CivilDate, to: CivilDate): CivilDate[] {
  if (from > to) return [];
  const out: CivilDate[] = [];
  const [fy, fm] = from.split("-").map(Number) as [number, number];
  const [ty] = to.split("-").map(Number) as [number];

  if (rule.frequency === "weekly") {
    const weekday = rule.weekday ?? 0;
    const current = new Date(`${from}T00:00:00Z`).getUTCDay();
    for (let d = addDays(from, (weekday - current + 7) % 7); d <= to; d = addDays(d, 7)) out.push(d);
    return out;
  }

  if (rule.frequency === "monthly") {
    const day = rule.dayOfMonth ?? 1;
    for (let y = fy, m = fm; ; ) {
      const d = clamped(y, m, day);
      if (d > to) break;
      if (d >= from) out.push(d);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return out;
  }

  const month = rule.month ?? 1;
  const day = rule.dayOfMonth ?? 1;
  for (let y = fy; y <= ty; y++) {
    const d = clamped(y, month, day);
    if (d >= from && d <= to) out.push(d);
  }
  return out;
}

/** Ocorrências de uma recorrência em [from, to], respeitando início, fim e o que o cron já gerou. */
function within(r: Recurrence, from: CivilDate, to: CivilDate): CivilDate[] {
  let start = r.startDate > from ? r.startDate : from;
  if (r.lastGeneratedDate) {
    const afterGenerated = addDays(r.lastGeneratedDate, 1);
    if (afterGenerated > start) start = afterGenerated;
  }
  const end = r.endDate && r.endDate < to ? r.endDate : to;
  return occurrencesBetween(r, start, end);
}

/** Próxima data a partir de `today` (inclusive), ou `null` se a recorrência já terminou. */
export function nextOccurrence(r: Recurrence, today: CivilDate): CivilDate | null {
  // Uma janela de 400 dias cobre qualquer frequência (a anual repete em até 366 dias).
  const from = r.startDate > today ? r.startDate : today;
  return within(r, from, addDays(from, 400))[0] ?? null;
}

/** Recorrências que vencem nos próximos `days` dias (hoje inclusive), em ordem de data. */
export function upcoming(rules: Recurrence[], today: CivilDate, days: number) {
  const to = addDays(today, days - 1);
  return rules
    .flatMap((recurrence) => within(recurrence, today, to).map((date) => ({ recurrence, date })))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
